import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Receipt } from 'lucide-react';
import mapi from './api';
import { MobileShell, MobileHeader, C } from './MobileShell';
import { formatDate, formatDeNumber } from '@/lib/utils';

const MONATE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const ZAHLUNG = {
  unpaid: 'Offen',
  paid: 'Bezahlt',
  partial: 'Teilweise bezahlt',
};

function abrechnungsmonat(iso) {
  if (!iso) return '';
  const [jahr, monat] = String(iso).slice(0, 10).split('-');
  const name = MONATE[Number(monat) - 1];
  return name ? `${name} ${jahr}` : '';
}

export default function MobileInvoices() {
  const [rows, setRows] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await mapi.get('/app/invoices');
      setRows(r.data || []);
    } catch (e) {
      toast.error('Abrechnungen konnten nicht geladen werden');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const openPdf = async (row) => {
    try {
      const r = await mapi.get(`/app/invoices/${row.id}/pdf`, { responseType: 'blob' });
      const blob = r.data instanceof Blob ? r.data : new Blob([r.data], { type: 'application/pdf' });
      const typed = blob.type ? blob : new Blob([blob], { type: 'application/pdf' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(typed));
    } catch (e) {
      toast.error('Die Rechnung konnte nicht geöffnet werden');
    }
  };

  return (
    <MobileShell>
      <MobileHeader title="Abrechnungen" onRefresh={load} backTo="/m" />
      <div className="flex-1 overflow-y-auto px-4 py-3 pb-10">
        {previewUrl && (
          <iframe
            title="Rechnung"
            src={previewUrl}
            className="w-full rounded-xl mb-3"
            style={{ height: 420, border: `1px solid ${C.border}`, background: C.card }}
            data-testid="mobile-invoice-preview"
          />
        )}
        {!rows.length && (
          <p className="text-center text-sm mt-10" style={{ color: C.dim }} data-testid="mobile-invoices-empty">
            Für dieses Konto sind keine Abrechnungen hinterlegt.
          </p>
        )}
        {rows.map((row) => {
          const monat = abrechnungsmonat(row.billing_period_start);
          const kinder = (row.student_names || []).join(', ');
          return (
            <div
              key={row.id}
              className="rounded-xl mb-3 px-4 py-3.5"
              style={{ background: C.card, border: `1px solid ${C.border}` }}
              data-testid={`mobile-invoice-${row.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold" style={{ color: C.text }}>
                    {monat || row.invoice_number}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: C.dim }}>{row.invoice_number}</p>
                </div>
                <p className="text-sm font-semibold shrink-0" style={{ color: C.text }}>
                  {formatDeNumber(row.total_amount)} €
                </p>
              </div>
              {(kinder || row.family_name) && (
                <p className="text-xs mt-2" style={{ color: C.dim }}>
                  {[kinder, row.family_name].filter(Boolean).join(' · ')}
                </p>
              )}
              <p className="text-xs mt-1" style={{ color: C.dim }}>
                {ZAHLUNG[row.payment_status] || row.payment_status}
                {row.due_date ? ` · fällig ${formatDate(row.due_date)}` : ''}
              </p>
              <button
                type="button"
                onClick={() => openPdf(row)}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold"
                style={{ color: C.red }}
                data-testid={`mobile-invoice-pdf-${row.id}`}
              >
                <Receipt className="w-3.5 h-3.5" /> Rechnung anzeigen
              </button>
            </div>
          );
        })}
      </div>
    </MobileShell>
  );
}
